import { STATUS_META, scoreColor } from "@/lib/format";
import { cn } from "@/lib/utils";

export function StatusBadge({ status, className, testId }: { status?: string | null; className?: string; testId?: string }) {
  const meta = STATUS_META[status || ""] || { label: status || "—", badge: "bg-slate-500/10 text-slate-400 border border-slate-500/20" };
  return (
    <span data-testid={testId || `status-badge-${(status || "none").toLowerCase()}`} className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-mono font-medium tracking-wide whitespace-nowrap", meta.badge, className)}>
      {meta.label}
    </span>
  );
}

export function ScoreRing({ score, size = 56, testId }: { score?: number | null; size?: number; testId?: string }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const v = score == null ? 0 : Math.max(0, Math.min(100, score));
  const color = scoreColor(score);
  return (
    <div data-testid={testId || "score-ring"} className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#26334d" strokeWidth={5} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={5} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * v) / 100} style={{ transition: "stroke-dashoffset 600ms ease" }} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center font-mono font-semibold" style={{ color, fontSize: size * 0.3 }}>
        {score == null ? "—" : Math.round(score)}
      </div>
    </div>
  );
}

export function KpiCard({ label, value, sub, icon, accent, testId, pulse }: { label: string; value: React.ReactNode; sub?: string; icon?: React.ReactNode; accent?: string; testId: string; pulse?: boolean }) {
  return (
    <div data-testid={testId} className="tg-card tg-card-hover p-5 flex flex-col gap-3 tg-enter">
      <div className="flex items-center justify-between">
        <span className="tg-label">{label}</span>
        <span className={cn("text-slate-400", pulse && "tg-pulse")} style={{ color: accent }}>{icon}</span>
      </div>
      <div className="font-display text-3xl font-semibold tracking-tight" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function EmptyState({ text, testId }: { text: string; testId?: string }) {
  return <div data-testid={testId || "empty-state"} className="tg-card p-10 text-center text-sm text-slate-400">{text}</div>;
}

export function Spinner() {
  return <div data-testid="loading-spinner" className="flex justify-center p-10"><div className="h-6 w-6 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" /></div>;
}

export function Mono({ children, className }: { children: React.ReactNode; className?: string }) {
  return <span className={cn("font-mono text-xs text-slate-300", className)}>{children}</span>;
}
